import React, { useMemo, useEffect, useCallback, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PracticeContentLayout, { TopicItem } from '../../components/common/PracticeContentLayout';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import ArticleRating from '../../components/common/ArticleRating';
import CommentSection from '../../components/common/CommentSection';
import { ArticleHeaderProvider } from '../../context/ArticleHeaderContext';
import { useSectionProgress } from '../../hooks/useSectionProgress';
import JSIntroduction from './JSIntroduction';
import JSVariables from './JSVariables';
import JSOperators from './JSOperators';
import JSControlFlow from './JSControlFlow';
import JSFunctions from './JSFunctions';
import JSScope from './JSScope';
import JSObjects from './JSObjects';
import JSArrays from './JSArrays';
import JSArrayMethods from './JSArrayMethods';
import JSDestructuring from './JSDestructuring';
import JSSpreadRest from './JSSpreadRest';
import JSES6Intro from './JSES6Intro';
import JSES6Quiz from './JSES6Quiz';
import JSArrowFunctions from './JSArrowFunctions';
import JSTemplateLiterals from './JSTemplateLiterals';
import JSClasses from './JSClasses';
import JSModules from './JSModules';
import JSAsyncIntro from './JSAsyncIntro';
import JSCallbacks from './JSCallbacks';
import JSPromises from './JSPromises';
import JSAsyncAwait from './JSAsyncAwait';
import JSPromisesAdvanced from './JSPromisesAdvanced';
import JSPromisesAdvanced2 from './JSPromisesAdvanced2';
import JSAsyncAwaitPatterns from './JSAsyncAwaitPatterns';
import JSFetchAPI from './JSFetchAPI';
import JSClosures from './JSClosures';
import JSThisKeyword from './JSThisKeyword';
import JSEventLoop from './JSEventLoop';
import JSPrototypes from './JSPrototypes';
import JSDomBasics from './JSDomBasics';
import JSErrorHandling from './JSErrorHandling';
import JSDomManipulation from './JSDomManipulation';
import JSSelectors from './JSSelectors';
import JSEvents from './JSEvents';

// Lazy-loaded standalone playground (NOT a lesson) — keeps the JS editor,
// engine, and worker out of the lessons' initial chunk.
const JavaScriptPlaygroundPage = React.lazy(() => import('./javascript/JavaScriptPlaygroundPage'));

interface JavaScriptContent {
  id: string;
  title: string;
  lastUpdated: string;
  author: {
    name: string;
    fullName: string;
    avatar?: string;
  };
  readTime: string;
  content: React.ReactNode;
}

// Define the topic structure
const topics: TopicItem[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    completed: false,
  },
  {
    id: 'fundamentals',
    title: 'Fundamentals',
    completed: false,
    children: [
      { id: 'variables', title: 'Variables & Data Types', completed: false },
      { id: 'operators', title: 'Operators & Expressions', completed: false },
      { id: 'control-flow', title: 'Control Flow', completed: false },
      { id: 'functions', title: 'Functions', completed: false },
      { id: 'scope', title: 'Scope & Hoisting', completed: false },
    ],
  },
  {
    id: 'objects-arrays',
    title: 'Objects & Arrays',
    completed: false,
    children: [
      { id: 'objects', title: 'Objects', completed: false },
      { id: 'arrays', title: 'Arrays', completed: false },
      { id: 'array-methods', title: 'Array Methods', completed: false },
      { id: 'destructuring', title: 'Destructuring', completed: false },
      { id: 'spread-rest', title: 'Spread & Rest Operators', completed: false },
    ],
  },
  {
    id: 'modern-js',
    title: 'Modern JavaScript (ES6+)',
    completed: false,
    children: [
      { id: 'es6-intro', title: 'Introduction to ES6+', completed: false },
      { id: 'arrow-functions', title: 'Arrow Functions', completed: false },
      { id: 'template-literals', title: 'Template Literals', completed: false },
      { id: 'classes', title: 'Classes', completed: false },
      { id: 'modules', title: 'Modules', completed: false },
      { id: 'es6-quiz', title: 'Section Quiz', completed: false },
    ],
  },
  {
    id: 'async-js',
    title: 'Async JavaScript',
    completed: false,
    children: [
      { id: 'async-intro', title: 'Introduction to Async', completed: false },
      { id: 'callbacks', title: 'Callbacks', completed: false },
      { id: 'promises', title: 'Promises', completed: false },
      { id: 'promises-advanced', title: 'Promises Advanced', completed: false },
      { id: 'promises-advanced-2', title: 'Promises Advanced (Part 2)', completed: false },
      { id: 'async-await', title: 'Async/Await', completed: false },
      { id: 'async-await-advanced', title: 'Async/Await Patterns', completed: false },
      { id: 'fetch-api', title: 'Fetch API', completed: false },
    ],
  },
  {
    id: 'advanced-js',
    title: 'Advanced JavaScript',
    completed: false,
    children: [
      { id: 'closures', title: 'Closures', completed: false },
      { id: 'this-keyword', title: 'this Keyword', completed: false },
      { id: 'prototypes', title: 'Prototypes & Inheritance', completed: false },
      { id: 'event-loop', title: 'Event Loop & Concurrency', completed: false },
      { id: 'error-handling', title: 'Error Handling', completed: false },
    ],
  },
  {
    id: 'browser-apis',
    title: 'Browser APIs',
    completed: false,
    children: [
      { id: 'dom-basics', title: 'DOM Basics', completed: false },
      { id: 'selectors', title: 'Selectors & Traversal', completed: false },
      { id: 'events', title: 'Event Handling', completed: false },
      { id: 'manipulation', title: 'DOM Manipulation', completed: false },
    ],
  },
  // Standalone JavaScript Playground (NOT a lesson)
  {
    id: 'javascript-playground',
    title: 'Playground',
    completed: false,
  },
];

// Helper function to find first leaf topic
const findFirstLeafTopic = (items: TopicItem[]): string | null => {
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      const childResult = findFirstLeafTopic(item.children);
      if (childResult) return childResult;
    } else {
      return item.id;
    }
  }
  return null;
};

const JavaScriptPractice: React.FC = () => {
  const { topicId } = useParams<{ topicId?: string }>();
  const navigate = useNavigate();
  const { isLessonComplete, markLessonComplete } = useSectionProgress('javascript');

  // Redirect to first topic if no topicId is provided
  useEffect(() => {
    if (!topicId) {
      const firstTopic = findFirstLeafTopic(topics);
      if (firstTopic) {
        navigate(`/languages/javascript/${firstTopic}`, { replace: true });
      }
    }
  }, [topicId, navigate]);

  // Calculate total chapters
  const calculateTotalChapters = useCallback((items: TopicItem[]): number => {
    let count = 0;
    for (const item of items) {
      if (!item.children || item.children.length === 0) {
        count++;
      } else {
        count += calculateTotalChapters(item.children);
      }
    }
    return count;
  }, []);

  const totalChapters = useMemo(() => calculateTotalChapters(topics), [calculateTotalChapters]);

  // Compute topics with progress-based completed state
  const topicsWithProgress: TopicItem[] = useMemo(() => {
    return topics.map((section) => {
      if (!section.children || section.children.length === 0) {
        return { ...section, completed: isLessonComplete(section.id) };
      }
      const updatedChildren = section.children.map((child) => ({
        ...child,
        completed: isLessonComplete(child.id),
      }));
      const allChildrenComplete = updatedChildren.every((c) => c.completed);
      return { ...section, children: updatedChildren, completed: allChildrenComplete };
    });
  }, [isLessonComplete]);

  const completedChapters = useMemo(() => {
    let count = 0;
    for (const section of topicsWithProgress) {
      if (!section.children) {
        if (section.completed) count++;
      } else {
        for (const child of section.children) {
          if (child.completed) count++;
        }
      }
    }
    return count;
  }, [topicsWithProgress]);

  // Get current topic ID
  const currentTopicId = topicId || findFirstLeafTopic(topics) || 'introduction';

  // Content for different topics
  const getContent = (id: string): JavaScriptContent => {
    const authorInfo = {
      name: 'Sachin',
      fullName: 'Sachin Saini',
      avatar: undefined,
    };

    const contents: Record<string, JavaScriptContent> = {
      // Standalone JavaScript Playground (NOT a lesson — no rating/comments).
      'javascript-playground': {
        id: 'javascript-playground',
        title: 'JavaScript Playground',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '',
        content: (
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-[40vh]">
                <div className="h-8 w-8 rounded-full border-2 border-amber-600 border-t-transparent animate-spin" />
              </div>
            }
          >
            <JavaScriptPlaygroundPage />
          </Suspense>
        ),
      },
      'introduction': {
        id: 'introduction',
        title: 'Introduction to JavaScript',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '9 min read',
        content: (
          <div className="max-w-none">
            <JSIntroduction author={authorInfo} />

          </div>
        ),
      },
      'intro-1': {
        id: 'intro-1',
        title: 'What is JavaScript?',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '8 min read',
        content: (
          <div className="max-w-none">
            {/* Article Header */}
            <div className="mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                What is JavaScript?
              </h1>
              <AuthorInfo
                name={authorInfo.name}
                fullName={authorInfo.fullName}
                avatar={authorInfo.avatar}
                lastUpdated="December 31, 2025"
                readTime="8 min read"
              />
            </div>

            {/* Introduction */}
            <section className="mb-8">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                JavaScript is a <strong>high-level, interpreted programming language</strong> that has become the backbone of modern web development.
                It's the only programming language that runs natively in web browsers, making it essential for creating interactive and dynamic web experiences.
              </p>
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                Originally created in just 10 days by Brendan Eich in 1995, JavaScript has evolved from a simple scripting language
                for web browsers into a powerful, versatile language used for frontend development, backend services, mobile apps,
                desktop applications, and even IoT devices.
              </p>
            </section>

            {/* Definition */}
            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Definition</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                JavaScript is a <strong>dynamic, prototype-based, multi-paradigm programming language</strong> that supports:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
                <li><strong>Object-oriented programming</strong> - Using objects and classes</li>
                <li><strong>Functional programming</strong> - Functions as first-class citizens</li>
                <li><strong>Imperative programming</strong> - Step-by-step instructions</li>
                <li><strong>Event-driven programming</strong> - Responding to user interactions</li>
              </ul>
            </section>

            {/* Why JavaScript Matters */}
            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Why JavaScript Matters</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                JavaScript's importance in modern software development cannot be overstated:
              </p>

              <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">1. Universal Web Language</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  JavaScript is the only programming language that runs natively in all modern web browsers.
                  This means any interactive feature on a website—from simple form validation to complex single-page applications—relies on JavaScript.
                </p>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 p-4 mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">2. Full-Stack Capability</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  With Node.js, JavaScript can run on servers, enabling developers to use the same language for both frontend and backend development.
                  This reduces context switching and allows code sharing between client and server.
                </p>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-purple-500 p-4 mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">3. Rich Ecosystem</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  JavaScript has the largest package ecosystem (npm) with millions of libraries and frameworks.
                  Frameworks like React, Vue, and Angular have revolutionized frontend development, while Express.js and Next.js
                  dominate backend and full-stack development.
                </p>
              </div>

              <div className="bg-orange-50 dark:bg-orange-900/20 border-l-4 border-orange-500 p-4 mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">4. Career Opportunities</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  JavaScript developers are in high demand across industries. According to various surveys, JavaScript consistently ranks
                  as one of the most popular and sought-after programming languages, with excellent job prospects and competitive salaries.
                </p>
              </div>
            </section>

            {/* Key Characteristics */}
            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Key Characteristics</h2>

              <div className="space-y-4">
                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">✨ Dynamic Typing</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    Variables don't need type declarations. JavaScript automatically determines the type at runtime.
                  </p>
                  <CodeBlock
                    snippets={[
                      {
                        language: 'javascript',
                        code: `let name = "JavaScript";  // string
let version = 2024;        // number
let isAwesome = true;      // boolean

// Type can change
name = 42;  // Now it's a number!`,
                      },
                    ]}
                    defaultLanguage="javascript"
                    id="dynamic-typing"
                  />
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">🚀 Interpreted Language</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    JavaScript is executed line by line by the JavaScript engine (like V8 in Chrome), without a separate compilation step.
                    This makes development faster and more flexible.
                  </p>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">🎯 Prototype-Based</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    JavaScript uses prototypes for inheritance instead of classes (though ES6 introduced class syntax as syntactic sugar).
                    Objects can inherit properties and methods from other objects.
                  </p>
                  <CodeBlock
                    snippets={[
                      {
                        language: 'javascript',
                        code: `// Prototype-based inheritance
function Animal(name) {
  this.name = name;
}

Animal.prototype.speak = function() {
  return \`\${this.name} makes a sound\`;
};

const dog = new Animal("Dog");
console.log(dog.speak()); // "Dog makes a sound"`,
                      },
                    ]}
                    defaultLanguage="javascript"
                    id="prototype-example"
                  />
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">⚡ Event-Driven</h3>
                  <p className="text-gray-700 dark:text-gray-300">
                    JavaScript excels at handling user interactions and events. It can respond to clicks, form submissions,
                    keyboard input, and many other browser events.
                  </p>
                  <CodeBlock
                    snippets={[
                      {
                        language: 'javascript',
                        code: `// Event-driven programming
button.addEventListener('click', function() {
  console.log('Button clicked!');
});

// Or with arrow function
button.addEventListener('click', () => {
  console.log('Button clicked!');
});`,
                      },
                    ]}
                    defaultLanguage="javascript"
                    id="event-driven"
                  />
                </div>
              </div>
            </section>

            {/* Where JavaScript Runs */}
            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Where JavaScript Runs</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">🌐 Web Browsers</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    The primary environment for JavaScript. Every modern browser includes a JavaScript engine:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 ml-4">
                    <li>Chrome/Edge: V8 engine</li>
                    <li>Firefox: SpiderMonkey</li>
                    <li>Safari: JavaScriptCore</li>
                  </ul>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">🖥️ Node.js</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    Allows JavaScript to run on servers and build backend applications. Uses the V8 engine outside the browser.
                  </p>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">📱 Mobile Apps</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    Frameworks like React Native and Ionic enable building native mobile apps using JavaScript.
                  </p>
                </div>

                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">🖥️ Desktop Apps</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-2">
                    Electron allows building cross-platform desktop applications using web technologies (HTML, CSS, JavaScript).
                  </p>
                </div>
              </div>
            </section>

            {/* A Simple Example */}
            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">A Simple Example</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Here's a simple JavaScript program that demonstrates basic concepts:
              </p>
              <CodeBlock
                snippets={[
                  {
                    language: 'javascript',
                    code: `// Variables
let greeting = "Hello";
const name = "World";

// Function
function sayHello(person) {
  return \`\${greeting}, \${person}!\`;
}

// Call the function
console.log(sayHello(name)); // Output: Hello, World!

// Array and iteration
const languages = ["JavaScript", "Python", "Java"];
languages.forEach(lang => {
  console.log(\`I love \${lang}\`);
});`,
                  },
                ]}
                defaultLanguage="javascript"
                id="simple-example"
              />
            </section>

            {/* Conclusion */}
            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Conclusion</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                JavaScript is more than just a programming language—it's the foundation of modern web development.
                Whether you're building interactive websites, server-side applications, mobile apps, or desktop software,
                JavaScript provides the tools and ecosystem to bring your ideas to life.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                In the next chapter, we'll explore the history and evolution of JavaScript, understanding how it grew from
                a 10-day project into one of the most important programming languages in the world.
              </p>
            </section>




          </div>
        ),
      },
      'intro-2': {
        id: 'intro-2',
        title: 'JavaScript History & Evolution',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '10 min read',
        content: (
          <div className="max-w-none">
            <div className="mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                JavaScript History & Evolution
              </h1>
              <AuthorInfo
                name={authorInfo.name}
                fullName={authorInfo.fullName}
                avatar={authorInfo.avatar}
                lastUpdated="December 31, 2025"
                readTime="10 min read"
              />
            </div>

            <section className="mb-8">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                JavaScript's journey from a 10-day prototype to the world's most widely used programming language is a fascinating story
                of innovation, competition, and evolution. Understanding this history helps us appreciate how JavaScript became what it is today.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">The Birth of JavaScript (1995)</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                In 1995, Netscape Communications Corporation was looking to add interactivity to web pages. They hired Brendan Eich,
                who was tasked with creating a scripting language for their Navigator browser. Remarkably, Eich created the first version
                of JavaScript in just <strong>10 days</strong>.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Originally named "Mocha," then "LiveScript," it was finally renamed to "JavaScript" as a marketing move to capitalize
                on Java's popularity at the time. Despite the name similarity, JavaScript and Java are completely different languages.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">ECMAScript Standardization (1997)</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To prevent fragmentation and ensure JavaScript worked consistently across browsers, Netscape submitted JavaScript to ECMA International
                for standardization. The result was ECMAScript (ES), the official specification that JavaScript implementations follow.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>ECMAScript 1 (ES1)</strong> was released in 1997, establishing the foundation for JavaScript as we know it today.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">The Browser Wars</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                The late 1990s saw intense competition between Netscape Navigator and Microsoft Internet Explorer. Microsoft created JScript,
                their own implementation of JavaScript, which led to compatibility issues. This period highlighted the need for standardization.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Modern Era: ES6 and Beyond</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                <strong>ES6 (ES2015)</strong> was a game-changer, introducing major features like:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
                <li>Arrow functions</li>
                <li>Classes</li>
                <li>Modules</li>
                <li>Promises</li>
                <li>Template literals</li>
                <li>Destructuring</li>
                <li>Let and const</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Since ES6, JavaScript has adopted an annual release cycle, with new features added each year (ES2016, ES2017, ES2018, etc.).
              </p>
            </section>


          </div>
        ),
      },
      'intro-3': {
        id: 'intro-3',
        title: 'JavaScript in Modern Development',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '9 min read',
        content: (
          <div className="max-w-none">
            <div className="mb-8 pb-6 border-b border-gray-200 dark:border-gray-700">
              <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                JavaScript in Modern Development
              </h1>
              <AuthorInfo
                name={authorInfo.name}
                fullName={authorInfo.fullName}
                avatar={authorInfo.avatar}
                lastUpdated="December 31, 2025"
                readTime="9 min read"
              />
            </div>

            <section className="mb-8">
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
                JavaScript has evolved far beyond simple browser scripting. Today, it powers everything from web applications to mobile apps,
                desktop software, and even embedded systems. Let's explore JavaScript's role in modern development.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Frontend Development</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Modern frontend development is dominated by JavaScript frameworks and libraries:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">⚛️ React</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Component-based library for building user interfaces, developed by Facebook.
                  </p>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">🅰️ Angular</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Full-featured framework for building large-scale applications, developed by Google.
                  </p>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">💚 Vue.js</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                    Progressive framework known for its simplicity and ease of learning.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Backend Development</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Node.js revolutionized backend development by enabling JavaScript to run on servers:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
                <li><strong>Express.js</strong> - Minimalist web framework</li>
                <li><strong>Next.js</strong> - Full-stack React framework</li>
                <li><strong>Nest.js</strong> - Enterprise-grade Node.js framework</li>
                <li><strong>Fastify</strong> - High-performance web framework</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Mobile Development</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                JavaScript enables cross-platform mobile app development:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300 mb-4 ml-4">
                <li><strong>React Native</strong> - Build native mobile apps with React</li>
                <li><strong>Ionic</strong> - Hybrid mobile app framework</li>
                <li><strong>NativeScript</strong> - Build truly native apps</li>
              </ul>
            </section>


          </div>
        ),
      },
      'variables': {
        id: 'variables',
        title: 'Variables & Data Types',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '12 min read',
        content: (
          <div className="max-w-none">
            <JSVariables author={authorInfo} />

          </div>
        ),
      },
      'operators': {
        id: 'operators',
        title: 'Operators & Expressions',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '10 min read',
        content: (
          <div className="max-w-none">
            <JSOperators author={authorInfo} />

          </div>
        ),
      },
      'control-flow': {
        id: 'control-flow',
        title: 'Control Flow',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '11 min read',
        content: (
          <div className="max-w-none">
            <JSControlFlow author={authorInfo} />

          </div>
        ),
      },
      'functions': {
        id: 'functions',
        title: 'Functions',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '14 min read',
        content: (
          <div className="max-w-none">
            <JSFunctions author={authorInfo} />

          </div>
        ),
      },
      'scope': {
        id: 'scope',
        title: 'Scope & Hoisting',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '12 min read',
        content: (
          <div className="max-w-none">
            <JSScope author={authorInfo} />

          </div>
        ),
      },
      'objects': {
        id: 'objects',
        title: 'Objects',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '13 min read',
        content: (
          <div className="max-w-none">
            <JSObjects author={authorInfo} />

          </div>
        ),
      },
      'arrays': {
        id: 'arrays',
        title: 'Arrays',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '11 min read',
        content: (
          <div className="max-w-none">
            <JSArrays author={authorInfo} />

          </div>
        ),
      },
      'array-methods': {
        id: 'array-methods',
        title: 'Array Methods',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '15 min read',
        content: (
          <div className="max-w-none">
            <JSArrayMethods author={authorInfo} />

          </div>
        ),
      },
      'destructuring': {
        id: 'destructuring',
        title: 'Destructuring',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '10 min read',
        content: (
          <div className="max-w-none">
            <JSDestructuring author={authorInfo} />

          </div>
        ),
      },
      'spread-rest': {
        id: 'spread-rest',
        title: 'Spread & Rest Operators',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '9 min read',
        content: (
          <div className="max-w-none">
            <JSSpreadRest author={authorInfo} />

          </div>
        ),
      },
      'es6-intro': {
        id: 'es6-intro',
        title: 'Introduction to ES6+',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '12 min read',
        content: (
          <div className="max-w-none">
            <JSES6Intro author={authorInfo} />

          </div>
        ),
      },
      'es6-quiz': {
        id: 'es6-quiz',
        title: 'Section Quiz',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '6 questions',
        content: (
          <div className="max-w-none">
            <JSES6Quiz author={authorInfo} />
          </div>
        ),
      },
      'arrow-functions': {
        id: 'arrow-functions',
        title: 'Arrow Functions',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '8 min read',
        content: (
          <div className="max-w-none">
            <JSArrowFunctions author={authorInfo} />

          </div>
        ),
      },
      'template-literals': {
        id: 'template-literals',
        title: 'Template Literals',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '7 min read',
        content: (
          <div className="max-w-none">
            <JSTemplateLiterals author={authorInfo} />

          </div>
        ),
      },
      'classes': {
        id: 'classes',
        title: 'Classes',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '12 min read',
        content: (
          <div className="max-w-none">
            <JSClasses author={authorInfo} />

          </div>
        ),
      },
      'modules': {
        id: 'modules',
        title: 'Modules',
        lastUpdated: 'December 31, 2025',
        author: authorInfo,
        readTime: '10 min read',
        content: (
          <div className="max-w-none">
            <JSModules author={authorInfo} />

          </div>
        ),
      },
      'promises': {
        id: 'promises',
        title: 'Promises',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '16 min read',
        content: (
          <div className="max-w-none">
            <JSPromises author={authorInfo} />

          </div>
        ),
      },
      'async-await': {
        id: 'async-await',
        title: 'Async/Await',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '17 min read',
        content: (
          <div className="max-w-none">
            <JSAsyncAwait author={authorInfo} />

          </div>
        ),
      },
      'closures': {
        id: 'closures',
        title: 'Closures',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '17 min read',
        content: (
          <div className="max-w-none">
            <JSClosures author={authorInfo} />

          </div>
        ),
      },
      'this-keyword': {
        id: 'this-keyword',
        title: 'The "this" Keyword',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '18 min read',
        content: (
          <div className="max-w-none">
            <JSThisKeyword author={authorInfo} />

          </div>
        ),
      },
      'prototypes': {
        id: 'prototypes',
        title: 'Prototypes & Inheritance',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '20 min read',
        content: (
          <div className="max-w-none">
            <JSPrototypes author={authorInfo} />

          </div>
        ),
      },
      'event-loop': {
        id: 'event-loop',
        title: 'Event Loop & Concurrency',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '18 min read',
        content: (
          <div className="max-w-none">
            <JSEventLoop author={authorInfo} />

          </div>
        ),
      },
      'error-handling': {
        id: 'error-handling',
        title: 'Error Handling',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '20 min read',
        content: (
          <div className="max-w-none">
            <JSErrorHandling author={authorInfo} />

          </div>
        ),
      },
      'dom-basics': {
        id: 'dom-basics',
        title: 'DOM Basics',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '11 min read',
        content: (
          <div className="max-w-none">
            <JSDomBasics author={authorInfo} />

          </div>
        ),
      },
      'selectors': {
        id: 'selectors',
        title: 'Selectors & Traversal',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '14 min read',
        content: (
          <div className="max-w-none">
            <JSSelectors author={authorInfo} />

          </div>
        ),
      },
      'events': {
        id: 'events',
        title: 'Event Handling',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '18 min read',
        content: (
          <div className="max-w-none">
            <JSEvents author={authorInfo} />

          </div>
        ),
      },
      'manipulation': {
        id: 'manipulation',
        title: 'DOM Manipulation',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '12 min read',
        content: (
          <div className="max-w-none">
            <JSDomManipulation author={authorInfo} />

          </div>
        ),
      },
      'async-intro': {
        id: 'async-intro',
        title: 'Introduction to Asynchronous JavaScript',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '14 min read',
        content: (
          <div className="max-w-none">
            <JSAsyncIntro author={authorInfo} />

          </div>
        ),
      },
      'callbacks': {
        id: 'callbacks',
        title: 'Callbacks',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '15 min read',
        content: (
          <div className="max-w-none">
            <JSCallbacks author={authorInfo} />

          </div>
        ),
      },
      'promises-advanced': {
        id: 'promises-advanced',
        title: 'Promises Advanced',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '17 min read',
        content: (
          <div className="max-w-none">
            <JSPromisesAdvanced author={authorInfo} />

          </div>
        ),
      },
      'promises-advanced-2': {
        id: 'promises-advanced-2',
        title: 'Promises Advanced (Part 2)',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '18 min read',
        content: (
          <div className="max-w-none">
            <JSPromisesAdvanced2 author={authorInfo} />

          </div>
        ),
      },
      'async-await-advanced': {
        id: 'async-await-advanced',
        title: 'Async/Await Patterns',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '18 min read',
        content: (
          <div className="max-w-none">
            <JSAsyncAwaitPatterns author={authorInfo} />

          </div>
        ),
      },
      'fetch-api': {
        id: 'fetch-api',
        title: 'Fetch API',
        lastUpdated: 'June 2026',
        author: authorInfo,
        readTime: '18 min read',
        content: (
          <div className="max-w-none">
            <JSFetchAPI author={authorInfo} />

          </div>
        ),
      },
    };

    return contents[id] || contents['introduction'];
  };

  const handleTopicSelect = (selectedTopicId: string) => {
    navigate(`/languages/javascript/${selectedTopicId}`);
  };

  const currentContent = getContent(currentTopicId);
  const isPlayground = currentTopicId === 'javascript-playground';
  const lessonCompleted = isLessonComplete(currentTopicId);

  // Mark Complete button — exact same as System Design pages (DynamicContentPage)
  const markCompleteButton = (
    <button
      onClick={() => {
        if (lessonCompleted) {
          markLessonComplete(`__UNCOMPLETE__${currentTopicId}`);
        } else {
          markLessonComplete(currentTopicId);
        }
      }}
      className={`article-completion-button ${lessonCompleted ? 'is-complete' : 'is-incomplete'} inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors`}
    >
      {lessonCompleted ? (
        <>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Completed ✓
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Mark as Complete
        </>
      )}
    </button>
  );

  // Content with Rating + Mark Complete + Comments appended AFTER lesson content
  // Exact same structure as DynamicContentPage.tsx (System Design pages)
  const fullContent = isPlayground ? (
    currentContent.content
  ) : (
    <>
      <ArticleHeaderProvider articleId={`javascript:${currentTopicId}`}>
        {currentContent.content}
      </ArticleHeaderProvider>
      {/* Article Rating + Mark Complete — same as DynamicContentPage */}
      <div className="mt-12">
        <ArticleRating articleId={currentTopicId} />
        <div className="border-t border-gray-200 dark:border-gray-700 mt-6 pt-6">
          {markCompleteButton}
        </div>
      </div>
      {/* Comment Section */}
      <div className="mt-12">
        <CommentSection articleId={currentTopicId} />
      </div>
    </>
  );

  return (
    <PracticeContentLayout
      className="system-design-article-page language-article-page"
      title="Master JavaScript"
      totalChapters={totalChapters}
      completedChapters={completedChapters}
      topics={topicsWithProgress}
      currentTopicId={currentTopicId}
      onTopicSelect={handleTopicSelect}
      onToggleComplete={(topicId, completed) => {
        if (completed) {
          markLessonComplete(topicId);
        } else {
          markLessonComplete(`__UNCOMPLETE__${topicId}`);
        }
      }}
      content={<React.Fragment key={currentTopicId}>{fullContent}</React.Fragment>}
    />
  );
};

export default JavaScriptPractice;
